import classNames from 'classnames';
import {
  findImageProvider,
  ImageProvider,
} from 'components/modulab/imageLoader';
import {
  Breakpoint,
  BREAKPOINTS,
  createMediaQuery,
  Responsive,
} from 'hooks/createMediaQuery';
import React, { CSSProperties } from 'react';

export type PictureProps = {
  alt: string | null | undefined;
  /**
   * Determines the resizing behavior
   */
  layout: 'fill' | 'fixed' | 'intrinsic' | 'responsive';
  /**
   * Determines how the image is loaded
   * @default lazy
   */
  loading?: 'eager' | 'lazy';
  /**
   * Determines how the image will be shown if it doesn't match the given
   * dimensions
   * @default contain
   */
  objectFit?:
    | 'contain'
    | 'cover'
    | 'fill'
    | 'none'
    | 'scale-down'
    | undefined
    | null;
  /**
   * Determines the image position within the given space
   * @default center (same as 50% 50%)
   */
  objectPosition?: string | null | undefined;
  loader?: ImageProvider;
  /**
   * Browser hint to decide which image to load
   */
  sizes?: string | null | undefined;
  source: Responsive<Source> & { _: Source };
};

export type Source = {
  src: string | null | undefined;
  height?: number | null | undefined;
  width?: number | null | undefined;
};

/**
 * Image with a layout shift warning
 *
 * This will render something similar to a <picture> element. A warning is shown
 * when there is the possibility of layout shifting.
 *
 * Note: native support was not tracked on caniuse.com at the time of writing
 * (https://github.com/Fyrd/caniuse/issues/5791)
 */
export function Picture({
  alt,
  layout = 'intrinsic',
  loader,
  loading = 'lazy',
  objectFit,
  objectPosition,
  source,
  sizes,
}: PictureProps): JSX.Element {
  const description = alt ?? '';
  const style = { objectFit, objectPosition };

  const elements: Array<{
    className: string;
    mediaQuery: string;
    image: Source;
    key: string;
  }> = [];

  // Create Tailwind classes for specific breakpoints
  // tailwind safelist: sm1:hidden sm1:block md1:hidden md1:block lg1:hidden lg1:block xl1:hidden xl1:block
  let larger: Breakpoint | null = null;
  for (const current of BREAKPOINTS) {
    const child = source[current.name];
    if (!child?.src) continue;
    elements.unshift({
      className: classNames(
        current.name !== '_' && `hidden ${current.name}:block`,
        larger && `${larger.name}:hidden`,
      ),
      image: child,
      key: current.name,
      mediaQuery: createMediaQuery(current.min, larger?.min),
    });
    larger = current;
  }

  const children = elements.map(({ image, className, key, mediaQuery }) => (
    <Image
      key={key}
      // don't propagate the alt text when there are multiple sources
      alt={elements.length === 1 ? description : undefined}
      height={image.height ?? undefined}
      layout={layout}
      loader={loader}
      loading={loading}
      className={className}
      media={mediaQuery || undefined}
      src={image.src ?? undefined}
      style={style}
      width={image.width ?? undefined}
      sizes={sizes ?? undefined}
    />
  ));

  if (children.length === 1) {
    return children[0];
  }

  // Wrap multiple images into one element, so css selectors that expect a
  // single element work correctly.
  return (
    <div
      aria-label={description}
      className={classNames(layout === 'fill' && 'size-full')}
      role="img"
    >
      {children}
    </div>
  );
}

type ImageProps = {
  alt: string | undefined;
  height?: number;
  layout: PictureProps['layout'];
  loader: PictureProps['loader'];
  loading: PictureProps['loading'];
  className: string | undefined;
  media: string | undefined;
  sizes: string | undefined;
  src: string | undefined;
  style: Record<string, unknown>;
  width: number | undefined;
};
function Image({
  alt,
  className,
  media,
  height,
  layout,
  loader,
  loading = 'lazy',
  sizes,
  src,
  style,
  width,
}: ImageProps) {
  const responsive = layout === 'responsive' || layout === 'intrinsic';
  const provider = loader ?? findImageProvider(src);

  // Use an aspect-ratio hack while support for the native property is still
  // too low (https://caniuse.com/?search=aspect-ratio)
  const aspectRatioHack = Boolean(responsive && width && height);

  let srcSet;
  let optimizedSrc = src;
  if (src != null && provider != null) {
    srcSet = provider.srcSet(src, width);
    optimizedSrc = provider.src(src, width);
  }

  let image = (
    <>
      {loading === 'eager' && (
        <link rel="preload" as="image" imageSrcSet={srcSet} media={media} />
      )}
      <img
        alt={alt}
        className={classNames(
          // Add some text styling for the rare case the alt text is visible
          'object-contain font-label',
          // The media query will be on the wrapper div in the case where the
          // aspect-ratio is fixed
          aspectRatioHack || className,
          aspectRatioHack && 'size-full',
          !aspectRatioHack && responsive && 'w-full',
          aspectRatioHack && 'absolute top-0',
          layout === 'fill' &&
            'size-0 max-h-full min-h-full min-w-full max-w-full',
        )}
        decoding="async"
        height={height}
        // Always use lazy loading to prevent images being loaded while they are
        // hidden by a media query, instead handle preloading using a <link>
        loading="lazy"
        sizes={sizes}
        src={optimizedSrc}
        srcSet={srcSet}
        style={
          Object.assign(
            {},
            style,
            layout === 'intrinsic' && { maxWidth: width ?? 'max-content' },
            layout === 'fixed' && {
              width: `${width}px`,
              height: `${height}px`,
              maxWidth: 'unset',
            },
          ) as CSSProperties
        }
        width={width}
      />
    </>
  );

  if (aspectRatioHack) {
    image = (
      <div
        className={classNames(
          'relative',
          className,
          layout === 'responsive' && 'w-full',
        )}
        style={{ paddingTop: `${(100 * (height ?? NaN)) / (width ?? NaN)}%` }}
      >
        {image}
      </div>
    );
  }

  // Show a warning message if the dimensions are missing
  if (
    process.env.NODE_ENV === 'development' &&
    layout !== 'fill' &&
    (width == null || height == null)
  ) {
    return (
      <div
        className={classNames(
          'relative table',
          className,
          layout === 'responsive' && 'w-full',
        )}
      >
        {image}
        <div className="absolute inset-0 bg-alert-error/30">
          <div className="absolute bg-alert-error p-4 text-left text-light font-label">
            warning: layout shift
          </div>
        </div>
      </div>
    );
  }

  return image;
}
